import { getReadabilityMetrics } from '../utils/readability';
import HR from 'UIComponents/elements/HR';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIIllustration from 'UIComponents/image/UIIllustration';
import UIFlex from 'UIComponents/layout/UIFlex';
import { useTracker } from '../../../context/hooks';
import { useEffect } from 'react';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import { FormatClamped } from './FormatClamped';
import { ArtifactsWithNoGatherer } from '../../../constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function EmptyState() {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: "p-x-6 p-top-6",
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "center",
        gap: 14,
        direction: "column",
        wrap: "wrap",
        children: [/*#__PURE__*/_jsx(UIIllustration, {
          name: "empty-state-associations",
          width: "50%"
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "xray-recommendation-lib.readability.emptyState"
          })
        })]
      })
    }), /*#__PURE__*/_jsx(HR, {})]
  });
}
function FormatSecondsToReadingTime({
  seconds
}) {
  if (isNaN(seconds) || seconds < 0) {
    return null;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  if (minutes === 0) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.readingTimeDescription.secondsOnly",
      options: {
        count: remainingSeconds
      }
    });
  } else {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.readingTimeDescription.minutesAndSeconds",
      options: {
        count: minutes,
        seconds: remainingSeconds
      }
    });
  }
}
function GradeLevel({
  score
}) {
  if (score >= 90) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.gradeLevel.5thGradeAndBelow"
    });
  } else if (score >= 80) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.gradeLevel.6thGrade"
    });
  } else if (score >= 70) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.gradeLevel.7thGrade"
    });
  } else if (score >= 60) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.gradeLevel.8thAnd9thGrade"
    });
  } else if (score >= 50) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.gradeLevel.10thTo12thGrade"
    });
  } else if (score >= 30) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.gradeLevel.college"
    });
  } else {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.gradeLevel.collegeGraduateAndBeyond"
    });
  }
}
function ReadabilityDescription({
  score
}) {
  if (score >= 90) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.easeDescription.veryEasy"
    });
  } else if (score >= 80) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.easeDescription.easy"
    });
  } else if (score >= 70) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.easeDescription.fairlyEasy"
    });
  } else if (score >= 60) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.easeDescription.plainEnglish"
    });
  } else if (score >= 50) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.easeDescription.fairlyDifficult"
    });
  } else if (score >= 30) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.easeDescription.difficult"
    });
  } else {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "xray-recommendation-lib.readability.easeDescription.veryDifficult"
    });
  }
}
const DisplayValueWithLabel = ({
  label,
  value
}) => {
  if (!value) return null;
  return /*#__PURE__*/_jsxs("p", {
    children: [/*#__PURE__*/_jsxs("b", {
      children: [label, " "]
    }), value]
  });
};
export const ReadabilitySection = ({
  artifacts
}) => {
  const postBody = artifacts[ArtifactsWithNoGatherer.POST_BODY_NO_MARKUP];
  const body = postBody || '';
  const {
    track
  } = useTracker();
  useEffect(() => {
    track('sectionView', {
      section: 'readability'
    });
  }, [track]);
  if (body.length <= 30) return /*#__PURE__*/_jsx(EmptyState, {});
  const scores = getReadabilityMetrics(body);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("section", {
      className: "p-x-6 p-top-6",
      children: [/*#__PURE__*/_jsx("p", {
        children: scores.fleschReadingEase ? /*#__PURE__*/_jsx(ReadabilityDescription, {
          score: scores.fleschReadingEase
        }) : null
      }), /*#__PURE__*/_jsx(DisplayValueWithLabel, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "xray-recommendation-lib.readability.readingLevel"
        }),
        value: /*#__PURE__*/_jsx(GradeLevel, {
          score: scores.fleschReadingEase
        })
      }), /*#__PURE__*/_jsx(DisplayValueWithLabel, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "xray-recommendation-lib.readability.readingEase.label"
        }),
        value: /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(FormatClamped, {
            value: scores.fleschReadingEase,
            min: 0,
            max: 100
          }), /*#__PURE__*/_jsx(UIHelpIcon, {
            className: "m-left-2",
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "xray-recommendation-lib.readability.readingEase.toolTip"
            }),
            tooltipPlacement: "right"
          })]
        })
      }), /*#__PURE__*/_jsx(DisplayValueWithLabel, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "xray-recommendation-lib.readability.readingTime.label"
        }),
        value: /*#__PURE__*/_jsxs("span", {
          children: [/*#__PURE__*/_jsx(FormatSecondsToReadingTime, {
            seconds: scores.readingTime
          }), /*#__PURE__*/_jsx(UIHelpIcon, {
            className: "m-left-1",
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "xray-recommendation-lib.readability.readingTime.toolTip"
            }),
            tooltipPlacement: "right"
          })]
        })
      }), /*#__PURE__*/_jsx(DisplayValueWithLabel, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "xray-recommendation-lib.readability.averageWordLength"
        }),
        value: scores.wordLength
      }), /*#__PURE__*/_jsx(DisplayValueWithLabel, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "xray-recommendation-lib.readability.characterCount"
        }),
        value: scores.characterCount
      })]
    }), /*#__PURE__*/_jsx(HR, {})]
  });
};